import { doc, setDoc } from "firebase/firestore";
import { db } from "../../../backend/firebaseConfig";
import { subscribeAdviserFullDetailsByEmailOrId } from "./AdviserLiveDetails";
import { deletePassportImages, getPassportEntries, uploadPassportImages, validatePassportImages } from "./passportImages";

export const getAdviserDocPaths = (adviserId) => {
  if (!adviserId) {
    throw new Error("Adviser id is required.");
  }

  return {
    adviserRef: doc(db, "Authorized Members", "Advisers", "Data", adviserId),

    personalDetailsRef: doc(
      db,
      "Authorized Members",
      "Advisers",
      "Data",
      adviserId,
      "Personal Details",
      "Data",
    ),

    applicationDetailsRef: doc(
      db,
      "Authorized Members",
      "Advisers",
      "Data",
      adviserId,
      "Application Details",
      "Data",
    ),

    nextOfKinRef: doc(
      db,
      "Authorized Members",
      "Advisers",
      "Data",
      adviserId,
      "Application Details",
      "Next of Kin Details",
    ),
  };
};

const prepopulateBrandColours = (adviserDoc = {}) => {
  const withoutHash = (value) => String(value ?? "").trim().replace(/^#/, "");
  const primary = withoutHash(adviserDoc.PrimaryColor ?? adviserDoc.primaryColor);
  const expandedPrimary = primary.length === 3
    ? primary.split("").map((character) => character + character).join("")
    : primary;
  const lighterShade = (strength) => {
    if (!/^[0-9a-f]{6}$/i.test(expandedPrimary)) return "";
    return [0, 2, 4].map((offset) =>
      Math.round(parseInt(expandedPrimary.slice(offset, offset + 2), 16) * strength + 255 * (1 - strength))
        .toString(16).padStart(2, "0"),
    ).join("").toUpperCase();
  };
  return {
    ...adviserDoc,
    PrimaryColor: primary,
    SecondaryColor: withoutHash(adviserDoc.SecondaryColor ?? adviserDoc.secondaryColor) || lighterShade(0.7),
    TertiaryColor: withoutHash(adviserDoc.TertiaryColor ?? adviserDoc.tertiaryColor) || lighterShade(0.4),
  };
};

export const buildEditAdviserForm = (data = {}) => ({
  adviserDoc: prepopulateBrandColours(data?.adviserDoc || {}),
  personalDetails: {
    ...(data?.personalDetails || {}),
    preferredName:
      data?.personalDetails?.preferredName ??
      data?.adviserDoc?.preferredName ??
      "",
  },
  applicationDetails: data?.applicationDetails || {},
  nextOfKinDetails: data?.nextOfKinDetails || {},
});

export const subscribeEditAdviserForm = (adviserKey, callback) => {
  return subscribeAdviserFullDetailsByEmailOrId(adviserKey, (data) => {
    callback({
      details: data,
      form: buildEditAdviserForm(data),
    });
  });
};

export const saveEditAdviserForm = async (adviserId, form = {}) => {
  const adviserDoc = { ...(form.adviserDoc || {}) };
  for (const [key, legacyKey, label] of [
    ["PrimaryColor", "primaryColor", "Primary colour"],
    ["SecondaryColor", "secondaryColor", "Secondary colour"],
    ["TertiaryColor", "tertiaryColor", "Tertiary colour"],
  ]) {
    const raw = adviserDoc[key] ?? adviserDoc[legacyKey];
    if (raw == null) continue;
    const value = String(raw).trim().replace(/^#/, "");
    if (value && !/^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) {
      throw new Error(`${label} must be a valid 3- or 6-digit hex code, or left empty.`);
    }
    adviserDoc[key] = value;
    // Keep older consumers of primaryColor in sync with the edited value.
    if (key === "PrimaryColor") adviserDoc.primaryColor = value;
  }
  const {
    adviserRef,
    personalDetailsRef,
    applicationDetailsRef,
    nextOfKinRef,
  } = getAdviserDocPaths(adviserId);

  const passports = getPassportEntries(adviserDoc);
  for (const passport of passports) {
    const pending = form.passportImageFilesById?.[passport.id] || [];
    validatePassportImages(passport.images || [], pending);
    const isSelf = passport.isSelf || passport.id === "legacy-passport";
    const hasImages = Boolean(passport.images?.length || pending.length);
    const started = passport.expiryDate || hasImages || (!isSelf &&
      (String(passport.holderName || "").trim() || String(passport.relationship || "").trim()));
    if (!started) continue;
    const label = isSelf ? "Self passport" : `Passport for ${passport.holderName || "additional person"}`;
    if (!String(passport.holderName || "").trim()) throw new Error(`${label}: enter the passport holder name.`);
    if (!isSelf && !String(passport.relationship || "").trim()) throw new Error(`${label}: enter a relationship or role.`);
    if (!passport.expiryDate) throw new Error(`${label}: enter an expiry date.`);
    if (!hasImages) throw new Error(`${label}: add at least one attachment.`);
  }
  if (Array.isArray(adviserDoc.passports) || passports.length) {
    adviserDoc.passports = [];
    for (const passport of passports) {
      const uploaded = await uploadPassportImages(adviserId, form.passportImageFilesById?.[passport.id] || []);
      adviserDoc.passports.push({...passport, images: [...(passport.images || []), ...uploaded]});
    }
  }

  await Promise.all([
    setDoc(adviserRef, adviserDoc, { merge: true }),
    setDoc(personalDetailsRef, form.personalDetails || {}, { merge: true }),
    setDoc(applicationDetailsRef, form.applicationDetails || {}, {
      merge: true,
    }),
    setDoc(nextOfKinRef, form.nextOfKinDetails || {}, { merge: true }),
  ]);
  await deletePassportImages(adviserId, form.passportImagesToDelete || []);
};
